/**
 * The lobby's GA4 events (M2 spec §1).
 *
 * The repo's helper is `src/Utils/GAEvents.ts` (`event({action, params})` →
 * `window.gtag('event', …)`); this module is the one seam between the lobby and
 * it, for two reasons: the A/B needs `lobby_variant` stamped on EVERY lobby hit
 * so the arms can be compared, and the React Native app will hand its own
 * analytics client to this file rather than to a dozen call sites.
 *
 * The old screen logs NOTHING from `GameSearch`, `joinGame` or `challengeGame`
 * (no `GAEvents` / `logEvent` import anywhere under `Screens/PlayChess`), so
 * there are no old event names to mirror: the spec's fallback pair
 * (`lobby_play`, `lobby_challenge`) is what is emitted, plus the assignment hit.
 */
import {event} from '@/Utils/GAEvents';
import {
  getSessionSync,
  setSessionPref,
} from '@/Store/liveGame/platform/storage';

import {LobbyVariant} from './resolveLobbyVariant';

export type LobbyEvent =
  /** once per session, as soon as the variant is decided */
  | 'lobby_variant_assigned'
  /** a random-opponent seek went out */
  | 'lobby_play'
  /** a challenge went out (option '1' · '2' · '3') */
  | 'lobby_challenge'
  /** the result card's Rematch: the same challenge with the colours swapped */
  | 'lobby_rematch';

/** the variant every hit is stamped with; set once by `pages/home/index`. */
let variant: LobbyVariant | null = null;

export const setAnalyticsVariant = (next: LobbyVariant | null): void => {
  variant = next;
};

export function track(name: LobbyEvent, params: any = {}): void {
  try {
    event({action: name, params: {...params, lobby_variant: variant}});
  } catch (e) {
    // analytics must never break a render (GAEvents swallows its own errors,
    // but `window.gtag` can be missing entirely in a test or an embedded view)
  }
}

const ASSIGNED_KEY = 'cc_lobby_variant_logged';

/**
 * `lobby_variant_assigned` fires ONCE per tab session: the home page mounts
 * again after every game, and one hit per game would make the A/B's denominator
 * meaningless.
 */
export function trackAssignmentOnce(next: LobbyVariant): void {
  setAnalyticsVariant(next);
  if (getSessionSync(ASSIGNED_KEY) === next) {
    return;
  }
  setSessionPref(ASSIGNED_KEY, next);
  track('lobby_variant_assigned', {});
}

export default {track, trackAssignmentOnce, setAnalyticsVariant};
